import React, { useEffect, useMemo, useState } from "react";
import { KpiCard } from "../../../Component/AgentComponents/KpiCard";
import { SectionCard } from "../../../Component/AgentComponents/SectionCard";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Badge } from "../../../Component/AgentComponents/Badge";
import Pagination from "../../../Component/Common/Pagination";
import RefreshButton from "../../../Component/Common/RefreshButton";
import { BASE_URL } from "../../../config/api";
import { PlusCircle } from "lucide-react";


function AgentShortcutCard({ onCreateLead }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3 h-full">
      <p className="text-[12px] font-bold uppercase tracking-wide text-slate-900 mb-2">
        Quick Actions
      </p>

      <div className="grid grid-cols-1 gap-2">
        <ShortcutItem
          label="Create Lead"
          icon={<PlusCircle className="w-4 h-4" />}
          color="blue"
          onClick={onCreateLead}
        />
      </div>
    </div>
  );
}

function ShortcutItem({ label, icon, color, onClick }) {
  const colorMap = {
    blue: "bg-blue-100 text-blue-700 hover:bg-blue-50",
    emerald: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    slate: "bg-slate-50 text-slate-700 hover:bg-slate-100",
  };

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg transition ${colorMap[color]}`}
    >
      {icon}
      <span className="text-[13px] font-medium text-center leading-tight">
        {label}
      </span>
    </button>
  );
}
export default function DashboardView({
  kpis,
  commissionTrend,
  retailerCommissionChartData,
  retailers,
  setCurrentView,
  setFilterType,
}) {

  const [dashboardKpis, setDashboardKpis] = useState({
  totalOnboarded: 0,
  sellingRetailers: 0,
  monthSales: 0,
  monthCommission: 0,
  lifetimeCommission: 0,
  avgSalesPerRetailer: 0,
});


useEffect(() => {
  const fetchAgentDashboard = async () => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUserData"));
      const agentId = authUser?.id;

      if (!agentId) return;

      const res = await fetch(
        `${BASE_URL}warranty/agent-dashboard?agent_id=${agentId}`
      );


      const json = await res.json();

      if (!res.ok || !json.status) {
        throw new Error("Failed to fetch agent dashboard");
      }

      const d = json.data;

      setDashboardKpis({
        totalOnboarded: d.onboarded_retailers?.total ?? 0,
        sellingRetailers: d.selling_retailers?.last_7_days ?? 0,
        monthSales: d.this_month_sales?.amount ?? 0,
        monthCommission: d.this_month_commission?.amount ?? 0,
        lifetimeCommission: d.lifetime_commission?.amount ?? 0,
        avgSalesPerRetailer: d.avg_sales_per_retailer?.amount ?? 0,
      });

    } catch (err) {
      console.error("Agent dashboard error:", err);
    }
  };

  fetchAgentDashboard();
}, []);

  const safeRetailers = retailers || [];
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 1;
  const focusRetailers = safeRetailers.filter(
  (r) => !r.isSelling || r.monthlySales < 20000
);

  const totalPages = Math.ceil(focusRetailers.length / itemsPerPage);

  const paginatedRetailers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return focusRetailers.slice(start, start + itemsPerPage);
  }, [focusRetailers, currentPage]);

  useEffect(() => {
  setCurrentPage(1);
}, [focusRetailers.length]);

  

  return (
    <div className="space-y-4">
      {/* KPIs Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Onboarded Retailers"
          value={dashboardKpis.totalOnboarded}
          subLabel={`${dashboardKpis.sellingRetailers} selling`}
        />

        <KpiCard
          label="Selling Retailers"
          value={dashboardKpis.sellingRetailers}
          subLabel={`${dashboardKpis.totalOnboarded - dashboardKpis.sellingRetailers} not selling`}
        />

        <KpiCard
          label="This Month Sales"
          value={`₹${(dashboardKpis.monthSales / 1000).toFixed(1)}k`}
          subLabel="Current month"
        />

        <KpiCard
          label="This Month Commission"
          value={`₹${dashboardKpis.monthCommission}`}
          subLabel={`Avg / retailer: ₹${dashboardKpis.avgSalesPerRetailer}`}
        />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            label="Lifetime Commission"
            value={`₹${dashboardKpis.lifetimeCommission}`}
            subLabel="All time earnings"
          />

          <KpiCard
            label="Activation Rate"
            value={
              dashboardKpis.totalOnboarded
                ? `${Math.round(
                    (dashboardKpis.sellingRetailers /
                      dashboardKpis.totalOnboarded) *
                      100
                  )}%`
                : "0%"
            }
            subLabel="Selling vs onboarded"
          />

          <KpiCard
            label="Avg Sales / Retailer"
            value={`₹${dashboardKpis.avgSalesPerRetailer}`}
            subLabel="Current month"
          />
          <AgentShortcutCard
            onCreateLead={() => navigate("/empdashboard")}
          />
        </div>


      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title="Monthly Commission Trend"
          right={
            <button
              onClick={() => setCurrentView("commission")}
              className="text-xs text-blue-600 hover:underline"
            >
              View report →
            </button>
          }
        >
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={commissionTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="commission"
                  name="Commission"
                  stroke="#2563eb"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="target"
                  name="Target"
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Retailer-wise Commission (This Month)">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={retailerCommissionChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="monthCommission"
                  name="Commission"
                  fill="#2563eb"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Focus list */}
      <SectionCard title="Suggested Focus Retailers">
        <div className="flex justify-between gap-2">
          <div className="text-xs text-slate-600 mb-2">
          Based on low sales / not selling yet.
        </div>
        <RefreshButton/>
        </div>

        <div className="space-y-2 text-xs max-h-48 overflow-y-auto pr-1">
          {paginatedRetailers.map((r) => (
              <div
                key={r.id}
                className="border border-slate-100 bg-white rounded-xl p-2 flex justify-between items-center"
              >
                <div>
                  <div className="font-semibold text-slate-800">{r.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {r.city} · Code: {r.code}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    This month sales:{" "}
                    <span className="font-medium text-slate-700">
                      ₹{r.monthlySales.toLocaleString()}
                    </span>
                    , Commission:{" "}
                    <span className="font-medium text-blue-700">
                      ₹{r.monthCommission.toLocaleString()}
                    </span>
                  </div>
                </div>
                <Badge color={r.isSelling ? "yellow" : "red"}>
                  {r.isSelling ? "Low activity" : "Not selling"}
                </Badge>
              </div>    
            ))}
            {totalPages > 1 && (
                <div className="flex justify-end mt-3">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
          {safeRetailers.filter((r) => !r.isSelling || r.monthlySales < 20000)
            .length === 0 && (
            <div className="text-xs text-slate-500">
              All retailers are performing well this month.
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}